/**
 * Single source of truth for the product's release history.
 *
 * Rendered in-app by WhatsNewComponent and mirrored (verbatim, by convention)
 * into /CHANGELOG.md at the repo root. When you ship something, add an entry
 * here first — CHANGELOG.md is a transcription of this file, not the other
 * way around.
 *
 * Versioning: semantic (MAJOR.MINOR.PATCH).
 *   MAJOR — breaking change to data, APIs, or saved-config shape
 *   MINOR — new capability, backward compatible
 *   PATCH — bug fix only, no new behavior
 */

export type ReleaseChangeType = 'added' | 'improved' | 'fixed';

export interface ReleaseChange {
  type: ReleaseChangeType;
  text: string;
  /** Optional anchor into the in-app User Guide (kaveri-lite-help.html#anchor) for "how to use". */
  guideLink?: string;
}

export interface ReleaseNote {
  version: string;
  codename: string;
  date: string; // ISO yyyy-mm-dd
  changes: ReleaseChange[];
}

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    version: '1.1.0',
    codename: 'Spreadsheet Grid',
    date: '2026-07-03',
    changes: [
      {
        type: 'added',
        text: 'Keyboard navigation in the data preview grid — Arrow keys, Tab, and Enter move between cells like a spreadsheet.',
        guideLink: 'data-editing',
      },
      {
        type: 'added',
        text: 'Copy-paste directly from Excel — copy a range, paste into the grid, and it fills starting at the cell you clicked.',
        guideLink: 'data-editing',
      },
      {
        type: 'added',
        text: 'Range selection (drag or Shift-click) with type-once-to-fill: type a value and press Enter to apply it to the whole selection.',
        guideLink: 'data-editing',
      },
      {
        type: 'added',
        text: 'Fill-down — drag the small handle at the corner of a cell to copy its value into the rows below.',
        guideLink: 'data-editing',
      },
      {
        type: 'added',
        text: 'Column-aware editors — low-cardinality text columns (e.g. Gender) now edit via a dropdown of existing values instead of free text, cutting down on typos.',
        guideLink: 'data-editing',
      },
      {
        type: 'added',
        text: 'Undo/redo (Ctrl+Z / Ctrl+Y) for any edit, paste, or fill made before saving.',
        guideLink: 'data-editing',
      },
      {
        type: 'added',
        text: 'Find & Replace — replace a value across an entire column in one action.',
        guideLink: 'data-editing',
      },
    ],
  },
  {
    version: '1.0.0',
    codename: 'Foundation',
    date: '2026-07-03',
    changes: [
      // ── Rule Builder / multi-file ingestion ──────────────────────────
      {
        type: 'added',
        text: 'Rule Builder: upload multiple Excel/CSV files at once and configure each as "join" (merged into one entity on a business key) or "individual" (its own entity).',
        guideLink: 'data-builder',
      },
      {
        type: 'added',
        text: 'Automatic Excel structure analysis — sheets, columns, data types, and formula cells are detected and mapped to a DEAM entity schema you can review and edit before saving.',
        guideLink: 'data-builder',
      },

      // ── Data Builder: browsing & editing ─────────────────────────────
      {
        type: 'added',
        text: 'Inline cell editing directly in the data preview — click a cell, change it, and save straight to the database with one click.',
        guideLink: 'data-editing',
      },
      {
        type: 'added',
        text: 'Download the complete dataset (not just the visible page) as CSV or Excel, in a format the user chooses.',
        guideLink: 'data-builder',
      },


      // ── Computation engine ────────────────────────────────────────────
      {
        type: 'added',
        text: 'Dynamic Formula Builder for entity data: write formulas with a rich function library — conditionals (IF, IFERROR), text (LEFT/RIGHT/MID-equivalents, CONCAT, UPPER/LOWER/TRIM), math (ROUND, POWER, MOD, ABS), and date parts.',
        guideLink: 'formulas',
      },
      {
        type: 'added',
        text: 'Aggregate/statistical functions including weighted average, variance, standard deviation, median, and percentile — for business calculations like weighted scoring, not just simple sums.',
        guideLink: 'formulas',
      },
      {
        type: 'added',
        text: 'Preview formula results client-side before committing, then save the computed column to the database in one step.',
        guideLink: 'formulas',
      },
    ],
  },
];

/** Highest version currently defined — used to compare against what the user has last seen. */
export const CURRENT_VERSION = RELEASE_NOTES[0]?.version ?? '0.0.0';
